import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { Button } from "../../../ui/Button";
import { PageSpinner } from "../../../ui/Spinner";
import { Badge, EmptyInline, Panel, SectionTitle } from "./_ui";

type Props = { tenantId: string };

export function AuditTab({ tenantId }: Props) {
  const listQ = useQuery({
    queryKey: ["tenant-audit", tenantId],
    queryFn: () => api.audit.list(tenantId, 200),
  });

  const verifyMut = useMutation({
    mutationFn: () => api.audit.verify(tenantId),
  });

  if (listQ.isLoading) return <PageSpinner />;
  const rows = listQ.data ?? [];

  return (
    <div className="space-y-5">
      <Panel accent={verifyMut.data ? (verifyMut.data.valid ? "success" : "danger") : undefined}>
        <SectionTitle
          icon={ICON.shieldCheck}
          desc="Replay complet du journal d'audit pour détecter toute altération."
          action={
            <Button
              variant="secondary"
              onClick={() => verifyMut.mutate()}
              disabled={verifyMut.isPending}
            >
              {verifyMut.isPending ? "Vérification…" : "Vérifier la chaîne"}
            </Button>
          }
        >
          Vérification chaîne hash
        </SectionTitle>
        {verifyMut.data ? (
          <div className="rounded-xl bg-navy-50/60 p-4 ring-1 ring-navy-100">
            {verifyMut.data.valid ? (
              <div className="flex items-center gap-2">
                <Badge tone="emerald" dot>
                  Chaîne valide
                </Badge>
                <span className="text-sm text-navy-500">
                  {verifyMut.data.count} entrées
                </span>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge tone="red" dot>
                    Chaîne corrompue
                  </Badge>
                  <span className="text-sm text-navy-500">
                    {verifyMut.data.count} entrées
                  </span>
                </div>
                <p className="text-xs text-red-600">
                  Première rupture : {verifyMut.data.brokenAt}
                </p>
              </div>
            )}
          </div>
        ) : null}
      </Panel>

      <Panel padded={false}>
        <h3 className="flex items-center gap-2 px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-navy-500">
          <span className="text-navy-400">{ICON.list}</span>
          Journal d'audit
        </h3>
        {rows.length === 0 ? (
          <div className="p-5">
            <EmptyInline>Aucun évènement enregistré.</EmptyInline>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-navy-100 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                <th className="px-5 py-2.5 font-bold">Date</th>
                <th className="px-5 py-2.5 font-bold">Évènement</th>
                <th className="px-5 py-2.5 font-bold">Acteur</th>
                <th className="px-5 py-2.5 font-bold">Hash</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {rows.map((r) => (
                <tr key={r.id} className="transition-colors hover:bg-navy-50/40">
                  <td className="px-5 py-3 text-xs text-navy-500 whitespace-nowrap">
                    {new Date(r.occurredAt).toLocaleString("fr-FR", {
                      dateStyle: "short",
                      timeStyle: "medium",
                    })}
                  </td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-xs text-navy-800">
                      {r.eventName}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {r.actorId ? (
                      <span className="font-mono text-[10px] text-navy-500">
                        {r.actorId.slice(0, 8)}…
                      </span>
                    ) : (
                      <span className="text-navy-400">-</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-[10px] text-navy-400">
                      {r.hash.slice(0, 12)}…
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  shieldCheck: (
    <svg {...sp}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  list: (
    <svg {...sp}>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
};
